import api from '@/lib/api';

interface Notification {
  _id: string;
  userId: string;
  type: string;
  title: string;
  message: string;
  read: boolean;
  date: string;
  createdAt: string;
  data?: any;
  severity?: string;
}

class NotificationService {
  private static instance: NotificationService;
  private notifications: Notification[] = [];
  private lastSync: number = 0;
  private syncInterval: number = 30000; // 30 seconds

  private constructor() {
    // Initialize notifications array
    this.notifications = [];
  }

  public static getInstance(): NotificationService {
    if (!NotificationService.instance) {
      NotificationService.instance = new NotificationService();
    }
    return NotificationService.instance;
  }

  public async fetchNotifications(): Promise<Notification[]> {
    try {
      console.log('Fetching notifications...');
      const response = await api.get('/api/notifications');
      if (response.data?.data?.notifications) {
        this.notifications = response.data.data.notifications;
        this.lastSync = Date.now();
        return this.notifications;
      }
      return [];
    } catch (error) {
      console.error('Error fetching notifications:', error);
      return [];
    }
  }

  public async markAsRead(notificationId: string): Promise<void> {
    try {
      await api.patch(`/notifications/${notificationId}/read`);
      this.notifications = this.notifications.map(notification =>
        notification._id === notificationId
          ? { ...notification, read: true }
          : notification
      );
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  }

  public async markAllAsRead(): Promise<void> {
    try {
      await api.patch('/notifications/read-all');
      this.notifications = this.notifications.map(notification => ({
        ...notification,
        read: true
      }));
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
    }
  }

  public async syncReadStatus(): Promise<void> {
    try {
      const unreadNotifications = this.notifications.filter(n => !n.read);
      if (unreadNotifications.length === 0) {
        console.log('Syncing notification read status with server (no-op)');
        return;
      }

      await Promise.all(
        unreadNotifications.map(notification =>
          this.markAsRead(notification._id)
        )
      );
    } catch (error) {
      console.error('Error syncing notification read status:', error);
    }
  }

  public getNotifications(): Notification[] {
    return this.notifications;
  }

  public getUnreadCount(): number {
    return this.notifications.filter(n => !n.read).length;
  }

  public shouldSync(): boolean {
    return Date.now() - this.lastSync > this.syncInterval;
  }

  public async ensureNotifications(): Promise<Notification[]> {
    if (this.notifications.length === 0 || this.shouldSync()) {
      return this.fetchNotifications();
    }
    return this.notifications;
  }
}

export default NotificationService.getInstance(); 